package hk.com.vr360_demo2.webapp.component.marzipano

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.marzipano.component.DraftHotspotEditorOverlay
import hk.com.vr360_demo2.webapp.component.marzipano.component.EditorHotspotIconWithTip
import js.coroutines.awaitCancellation
import kotlinx.browser.window as browserWindow
import kotlin.math.PI
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useRef
import web.cssom.Overflow
import web.cssom.Position
import web.cssom.px
import web.cssom.vh
import web.cssom.vw
import web.dom.document
import web.html.HTMLDivElement

/* ─── Imperative handle exposed to the parent via onHandleReady ─────────── */

class EditorViewerHandle(
    /** Capture current view params + snapshot (synchronous). */
    val captureView: () -> ViewCaptureResult?,
    /** Add a hotspot at the current view centre for the active scene. */
    val addHotspot: () -> Unit,
    /** Add a hotspot with explicit target scene and icon. */
    val addHotspotWithOptions: (HotspotCreateOptions) -> String?,
    /** Remove one hotspot by sceneId + hotspotId. */
    val removeHotspotById: (sceneId: String, hotspotId: String) -> Unit,
    /** Confirm one draft hotspot by sceneId + hotspotId. */
    val confirmPendingDraftHotspotById: (sceneId: String, hotspotId: String) -> Boolean,
    /** Enter draft-edit mode for one existing hotspot. */
    val beginEditHotspotById: (sceneId: String, hotspotId: String, options: HotspotCreateOptions) -> Boolean,
    /** Enter draft-move mode for one existing hotspot without changing options. */
    val beginMoveHotspotById: (sceneId: String, hotspotId: String) -> Boolean,
    /** Restore one hotspot to a previous snapshot. */
    val restoreHotspotById: (sceneId: String, hotspotId: String, snapshot: PanoramaLinkHotspot) -> Boolean,
    /** Read one draft hotspot by sceneId + hotspotId. */
    val getPendingDraftHotspotById: (sceneId: String, hotspotId: String) -> PanoramaLinkHotspot?,
    /** Read current hotspots of a given scene by its ID. */
    val getSceneHotspots: (sceneId: String) -> List<PanoramaLinkHotspot>,
    /** Animate the camera to the given yaw/pitch on the active scene. */
    val lookAt: (yaw: Double, pitch: Double) -> Unit,
)

data class HotspotCreateOptions(
    val targetSceneId: String,
    val iconUrl: String,
)

/* ─── Internal bookkeeping for a single live hotspot ────────────────────── */

private class HotspotTracker(
    val id: String,
    var yaw: Double,
    var pitch: Double,
    var target: String,
    var iconUrl: String? = null,
    var kind: HotspotKind = HotspotKind.Fixed,
    val wrapElement: web.dom.Element,
    val hotspot: Hotspot,
)

private enum class HotspotKind {
    Fixed,
    DraftEditable,
}

/* ─── Props ─────────────────────────────────────────────────────────────── */

external interface EditorPanoramaViewerProps : Props {
    var scenes: List<PanoramaSceneConfig>
    /** The ID of the scene that should be active. Resolved internally to an index. */
    var activeSceneId: String?
    var showHotspots: Boolean?
    var onSceneChange: ((sceneId: String) -> Unit)?
    /** Called once when the viewer is ready. The parent stores this handle in a ref. */
    var onHandleReady: ((EditorViewerHandle) -> Unit)?
    /** Called whenever the hotspot list for a scene changes (add / delete / drag / target / rotate). */
    var onHotspotsChange: ((sceneId: String, hotspots: List<PanoramaLinkHotspot>) -> Unit)?
    var pendingDraftSceneId: String?
    var pendingDraftHotspotId: String?
    var onConfirmPendingDraft: (() -> Unit)?
    var onCancelPendingDraft: (() -> Unit)?
}

/* ─── Constants ─────────────────────────────────────────────────────────── */

private const val HOTSPOT_ICON_URL = "/assets/images/hotspot/icon-1.png"

private fun clampIndex(index: Int, size: Int): Int {
    if (size <= 0) return 0
    return index.coerceIn(0, size - 1)
}

private fun imageSourceOptions(baseUrl: String): ImageUrlSourceOptions {
    val options = js("{}")
    options.cubeMapPreviewUrl = "$baseUrl/preview.jpg"
    return options.unsafeCast<ImageUrlSourceOptions>()
}

private fun initialViewParams(viewParameters: PanoramaViewParameters?): dynamic {
    val params = js("{}")
    params.yaw = viewParameters?.yaw ?: 0.0
    params.pitch = viewParameters?.pitch ?: 0.0
    params.fov = viewParameters?.fov ?: 3.14159
    return params
}

/** Reset a scene's view to [viewParameters] before [Scene.switchTo] so revisits use the configured initial view. */
private fun applyInitialViewToScene(scene: Scene, viewParameters: PanoramaViewParameters?) {
    try {
        val view = scene.asDynamic().view()
        val params = view.parameters()
        val initial = initialViewParams(viewParameters)
        params.yaw = initial.yaw
        params.pitch = initial.pitch
        params.fov = initial.fov
        view.setParameters(params)
    } catch (_: dynamic) {
        // Marzipano view API unavailable — keep whatever view state the scene already has
    }
}

private fun sceneOptions(
    source: Source,
    geometry: Marzipano.CubeGeometry,
    view: Marzipano.RectilinearView,
): SceneOptions {
    val options = js("{}")
    options.source = source
    options.geometry = geometry
    options.view = view
    options.pinFirstLevel = true
    return options.unsafeCast<SceneOptions>()
}

private fun switchOptions(transitionDuration: Int): SwitchOptions {
    val options = js("{}")
    options.transitionDuration = transitionDuration
    return options.unsafeCast<SwitchOptions>()
}

private fun hotspotPosition(yaw: Double, pitch: Double): HotspotPosition {
    val position = js("{}")
    position.yaw = yaw
    position.pitch = pitch
    return position.unsafeCast<HotspotPosition>()
}

private fun defaultLevels(): Array<Level> = arrayOf(
    level(tileSize = 256, size = 256, fallbackOnly = true),
    level(tileSize = 512, size = 512),
    level(tileSize = 512, size = 1024),
    level(tileSize = 512, size = 2048),
//    level(tileSize = 512, size = 4096)
)

private fun level(tileSize: Int, size: Int, fallbackOnly: Boolean? = null): Level {
    val level = js("{}")
    level.tileSize = tileSize
    level.size = size
    if (fallbackOnly != null) {
        level.fallbackOnly = fallbackOnly
    }
    return level.unsafeCast<Level>()
}

/**
 * Signature of scene *structure* (tiles / geometry only). Excludes [PanoramaSceneConfig.linkHotspots]
 * and [PanoramaSceneConfig.initialViewParameters] so that after a hotspot or initial-view save the
 * parent can update [PanoramaSceneConfig] without tearing down Marzipano and resetting the camera.
 */
private fun editorPanoramaStructureSignature(scenes: List<PanoramaSceneConfig>): String =
    scenes.joinToString("\u001f") { sc ->
        val levelsKey = sc.levels
            ?.joinToString(";") { lvl -> "${lvl.tileSize}_${lvl.size}_${lvl.fallbackOnly}" }
            .orEmpty()
        "${sc.id}\u001e${sc.url}\u001e${sc.faceSize}\u001e$levelsKey"
    }

/* ─── Component ─────────────────────────────────────────────────────────── */

val EditorPanoramaViewer = FC<EditorPanoramaViewerProps> { props ->
    val viewerRef = useRef<HTMLDivElement>()
    val viewerInstanceRef = useRef<Marzipano.Viewer>()
    val scenesRef = useRef<Array<Scene>>()
    val currentSceneIndexRef = useRef<Int>()
    val switchSceneRef = useRef<(Int, Boolean) -> Unit>()

    // Per-scene hotspot trackers: sceneIndex → MutableList<HotspotTracker>
    val trackersRef = useRef<MutableMap<Int, MutableList<HotspotTracker>>>()

    // ID → viewer-internal index mapping (built once per scene list)
    val sceneIdToIndexRef = useRef<Map<String, Int>>()

    // Keep latest props in a ref so imperative callbacks always see up-to-date values
    val propsRef = useRef<EditorPanoramaViewerProps>()
    propsRef.current = props

    // Counter for generating unique hotspot ids
    val hotspotIdCounter = useRef(0)
    fun nextHotspotId(): String {
        val c = (hotspotIdCounter.current ?: 0) + 1
        hotspotIdCounter.current = c
        return "h_${c}_${js("Date.now()")}"
    }

    /* ── Helper: collect PanoramaLinkHotspot list from trackers for a given scene ── */
    fun collectHotspots(sceneIndex: Int): List<PanoramaLinkHotspot> {
        val list = trackersRef.current?.get(sceneIndex) ?: return emptyList()
        return list
            .filter { it.kind == HotspotKind.Fixed }
            .map {
            PanoramaLinkHotspot(
                hotspotId = it.id,
                yaw = it.yaw,
                pitch = it.pitch,
                target = it.target,
                imageName = it.iconUrl,
            )
        }
    }

    fun notifyHotspotsChange(sceneIndex: Int) {
        val sceneId = propsRef.current?.scenes?.getOrNull(sceneIndex)?.id ?: return
        propsRef.current?.onHotspotsChange?.invoke(sceneId, collectHotspots(sceneIndex))
    }

    fun findTracker(sceneId: String, hotspotId: String): HotspotTracker? {
        val sceneIndex = sceneIdToIndexRef.current?.get(sceneId) ?: return null
        return trackersRef.current?.get(sceneIndex)?.firstOrNull { it.id == hotspotId }
    }

    /* ── Core: mount an editable hotspot onto a Marzipano scene ───────────── */
    fun mountEditorHotspot(
        sceneIndex: Int,
        tracker: HotspotTracker,
    ) {
        val hotspotWrap = tracker.wrapElement
        hotspotWrap.asDynamic().innerHTML = ""
        hotspotWrap.asDynamic().style.cursor = if (tracker.kind == HotspotKind.DraftEditable) "grab" else "default"
        hotspotWrap.asDynamic().onmousedown = null
        hotspotWrap.asDynamic().ontouchstart = null

        val React = js("require('react')")
        val reactDomClient = js("require('react-dom/client')")
        val targetSceneName = propsRef.current
            ?.scenes
            ?.firstOrNull { it.id == tracker.target }
            ?.name
            ?.takeIf { it.isNotBlank() }
            ?: tracker.target.takeIf { it.isNotBlank() }
            ?: "未設置目標場景"
        val iconRootElement = document.createElement("div")
        iconRootElement.asDynamic().style.width = "60px"
        iconRootElement.asDynamic().style.height = "60px"
        iconRootElement.asDynamic().className = "marzipano-tool__linkHotspotIcon"
        iconRootElement.asDynamic().style.pointerEvents = "auto"
        iconRootElement.asDynamic().style.cursor = if (tracker.kind == HotspotKind.DraftEditable) "grab" else "default"
        val iconProps = js("{}")
        iconProps.iconUrl = tracker.iconUrl ?: HOTSPOT_ICON_URL
        iconProps.tipText = targetSceneName
        val iconElement = React.createElement(EditorHotspotIconWithTip, iconProps)
        reactDomClient.createRoot(iconRootElement).render(iconElement)
        hotspotWrap.asDynamic().appendChild(iconRootElement)

        if (tracker.kind != HotspotKind.DraftEditable) return

        val updateDragPosition = fun(clientX: Double, clientY: Double) {
            val sceneList = scenesRef.current ?: return
            val active = sceneList.getOrNull(sceneIndex) ?: return
            val container = viewerRef.current ?: return
            val rect = container.asDynamic().getBoundingClientRect()

            val screenCoords = js("{}")
            screenCoords.x = clientX - (rect.left as Double)
            screenCoords.y = clientY - (rect.top as Double)

            val coords = try {
                active.asDynamic().view().screenToCoordinates(screenCoords)
            } catch (_: dynamic) { null } ?: return

            val yaw = coords.yaw as? Double ?: return
            val pitch = coords.pitch as? Double ?: return
            if (yaw.isNaN() || pitch.isNaN()) return

            tracker.hotspot.setPosition(hotspotPosition(yaw, pitch))
            tracker.yaw = yaw
            tracker.pitch = pitch
        }

        hotspotWrap.asDynamic().onmousedown = fun(event: dynamic) {
            if ((event.button as? Int) != 0) return
            event.preventDefault()
            event.stopPropagation()
            hotspotWrap.asDynamic().style.cursor = "grabbing"
            iconRootElement.asDynamic().style.cursor = "grabbing"
            viewerInstanceRef.current?.asDynamic()?.controls()?.disable()
            val wrapRect = hotspotWrap.asDynamic().getBoundingClientRect()
            val grabOffsetX = (event.clientX as Double) - (wrapRect.left as Double)
            val grabOffsetY = (event.clientY as Double) - (wrapRect.top as Double)
            val win = browserWindow.asDynamic()

            var onMouseMove: dynamic = null
            var onMouseUp: dynamic = null

            onMouseMove = fun(moveEvent: dynamic) {
                moveEvent.preventDefault()
                updateDragPosition(
                    (moveEvent.clientX as Double) - grabOffsetX,
                    (moveEvent.clientY as Double) - grabOffsetY
                )
            }

            onMouseUp = fun(_: dynamic) {
                win.removeEventListener("mousemove", onMouseMove)
                win.removeEventListener("mouseup", onMouseUp)
                hotspotWrap.asDynamic().style.cursor = "grab"
                iconRootElement.asDynamic().style.cursor = "grab"
                viewerInstanceRef.current?.asDynamic()?.controls()?.enable()
            }

            win.addEventListener("mousemove", onMouseMove)
            win.addEventListener("mouseup", onMouseUp)
        }

        hotspotWrap.asDynamic().ontouchstart = fun(event: dynamic) {
            val touchList = event.touches ?: return
            if ((touchList.length as Int) == 0) return
            event.stopPropagation()
            val touch = touchList[0]
            val wrapRect = hotspotWrap.asDynamic().getBoundingClientRect()
            val grabOffsetX = (touch.clientX as Double) - (wrapRect.left as Double)
            val grabOffsetY = (touch.clientY as Double) - (wrapRect.top as Double)
            viewerInstanceRef.current?.asDynamic()?.controls()?.disable()
            val win = browserWindow.asDynamic()

            var onTouchMove: dynamic = null
            var onTouchEnd: dynamic = null

            onTouchMove = fun(moveEvent: dynamic) {
                val moveTouchList = moveEvent.touches ?: return
                if ((moveTouchList.length as Int) == 0) return
                val mt = moveTouchList[0]
                moveEvent.preventDefault()
                updateDragPosition(
                    (mt.clientX as Double) - grabOffsetX,
                    (mt.clientY as Double) - grabOffsetY
                )
            }

            onTouchEnd = fun(_: dynamic) {
                win.removeEventListener("touchmove", onTouchMove)
                win.removeEventListener("touchend", onTouchEnd)
                viewerInstanceRef.current?.asDynamic()?.controls()?.enable()
            }

            win.addEventListener("touchmove", onTouchMove, js("({passive:false})"))
            win.addEventListener("touchend", onTouchEnd)
        }
    }

    fun confirmPendingDraftHotspot(sceneId: String, hotspotId: String): Boolean {
        val idx = sceneIdToIndexRef.current?.get(sceneId) ?: return false
        val trackerList = trackersRef.current?.get(idx) ?: return false
        val tracker = trackerList.firstOrNull { it.id == hotspotId && it.kind == HotspotKind.DraftEditable } ?: return false
        tracker.kind = HotspotKind.Fixed
        mountEditorHotspot(idx, tracker)
        notifyHotspotsChange(idx)
        return true
    }

    /* ── Main effect: create Marzipano viewer + scenes + initial hotspots ── */

    val scenesStructureSignature = editorPanoramaStructureSignature(props.scenes)

    useEffect(dependencies = arrayOf(scenesStructureSignature)) {
        val container = viewerRef.current ?: return@useEffect
        if (props.scenes.isEmpty()) {
            container.asDynamic().innerHTML = ""
            return@useEffect
        }

        // Prevent scrollbars
        document.documentElement.asDynamic().style.overflow = "hidden"
        document.documentElement.asDynamic().style.height = "100%"
        document.body.asDynamic().style.overflow = "hidden"
        document.body.asDynamic().style.height = "100%"
        document.body.asDynamic().style.margin = "0"
        document.body.asDynamic().style.padding = "0"

        container.asDynamic().innerHTML = ""
        val viewerOpts = js("{}")
        viewerOpts.stage = js("{}")
        viewerOpts.stage.preserveDrawingBuffer = true
        val viewer = Marzipano.Viewer(container, viewerOpts)
        viewerInstanceRef.current = viewer

        val allTrackers = mutableMapOf<Int, MutableList<HotspotTracker>>()
        trackersRef.current = allTrackers

        val sceneIndexesById = mutableMapOf<String, Int>()
        props.scenes.forEachIndexed { index, sc -> sceneIndexesById[sc.id] = index }
        sceneIdToIndexRef.current = sceneIndexesById

        switchSceneRef.current = switchScene@{ index: Int, notify: Boolean ->
            val sceneList = scenesRef.current ?: return@switchScene
            if (sceneList.isEmpty()) return@switchScene
            val safeIndex = clampIndex(index, sceneList.size)
            val initialView = propsRef.current?.scenes?.getOrNull(safeIndex)?.initialViewParameters
            applyInitialViewToScene(sceneList[safeIndex], initialView)
            sceneList[safeIndex].switchTo(switchOptions(300))
            currentSceneIndexRef.current = safeIndex
            if (notify) {
                val sceneId = propsRef.current?.scenes?.getOrNull(safeIndex)?.id
                if (sceneId != null) propsRef.current?.onSceneChange?.invoke(sceneId)
            }
        }

        val createdScenes = mutableListOf<Scene>()

        props.scenes.forEachIndexed { sceneIndex, sceneConfig ->
            val source = Marzipano.ImageUrlSource.fromString(
                "${sceneConfig.url}/{z}/{f}/{y}/{x}.jpg",
                imageSourceOptions(sceneConfig.url)
            )
            val geometry = Marzipano.CubeGeometry(sceneConfig.levels ?: defaultLevels())
            val limiter = Marzipano.RectilinearView.limit.traditional(sceneConfig.faceSize, (140 * PI) / 180)
            val view = Marzipano.RectilinearView(initialViewParams(sceneConfig.initialViewParameters), limiter)
            val scene = viewer.createScene(sceneOptions(source, geometry, view))

            val trackerList = mutableListOf<HotspotTracker>()
            allTrackers[sceneIndex] = trackerList

            // Mount initial hotspots
            sceneConfig.linkHotspots.orEmpty().forEach { h ->
                val wrap = document.createElement("div")
                wrap.asDynamic().className = "marzipano-tool__linkHotspot"
                wrap.asDynamic().style.display = "block"

                val pos = hotspotPosition(h.yaw, h.pitch)
                val mzHotspot = scene.hotspotContainer().createHotspot(wrap, pos)

                val tracker = HotspotTracker(
                    id = nextHotspotId(),
                    yaw = h.yaw,
                    pitch = h.pitch,
                    target = h.target,
                    iconUrl = h.imageName,
                    kind = HotspotKind.Fixed,
                    wrapElement = wrap,
                    hotspot = mzHotspot,
                )
                trackerList.add(tracker)
                mountEditorHotspot(sceneIndex, tracker)
            }

            createdScenes += scene
        }

        scenesRef.current = createdScenes.toTypedArray()

        if (createdScenes.isNotEmpty()) {
            val initialId = props.activeSceneId
            val initialIndex = if (initialId != null) sceneIndexesById[initialId] ?: 0 else 0
            switchSceneRef.current?.invoke(clampIndex(initialIndex, createdScenes.size), false)
        }

        /* ── Capture function ── */
        val captureCurrentView: () -> ViewCaptureResult? = captureCurrentView@{
            val v = viewerInstanceRef.current ?: return@captureCurrentView null
            val sceneList = scenesRef.current ?: return@captureCurrentView null
            if (sceneList.isEmpty()) return@captureCurrentView null
            val safeIndex = clampIndex(currentSceneIndexRef.current ?: 0, sceneList.size)
            val scene = sceneList[safeIndex]

            val viewParams = try {
                val p = scene.asDynamic().view().parameters()
                PanoramaViewParameters(yaw = p.yaw as? Double, pitch = p.pitch as? Double, fov = p.fov as? Double)
            } catch (_: dynamic) { PanoramaViewParameters() }

            val snapshot: String? = try {
                val stage = v.asDynamic().stage()
                val snap = stage?.takeSnapshot(js("({quality:80})"))?.unsafeCast<String>()
                if (snap != null && snap.length > 128) snap else null
            } catch (_: dynamic) { null }

            ViewCaptureResult(viewParams, snapshot)
        }

        /* ── Add-hotspot function ── */
        fun addHotspotInternal(
            targetOverride: String? = null,
            iconUrlOverride: String? = null,
            kind: HotspotKind = HotspotKind.Fixed,
        ): String? {
            val sceneList = scenesRef.current ?: return null
            val idx = currentSceneIndexRef.current ?: return null
            val safeIdx = clampIndex(idx, sceneList.size)
            val scene = sceneList[safeIdx]

            // Read current view centre
            val viewCenter = try {
                val p = scene.asDynamic().view().parameters()
                (p.yaw as? Double ?: 0.0) to (p.pitch as? Double ?: 0.0)
            } catch (_: dynamic) { 0.0 to 0.0 }

            // Default target = next scene
            val scenesCount = propsRef.current?.scenes?.size ?: 0
            val defaultTarget = if (scenesCount > 1) {
                val nextIdx = (safeIdx + 1) % scenesCount
                propsRef.current?.scenes?.get(nextIdx)?.id ?: ""
            } else ""
            val resolvedTarget = targetOverride ?: defaultTarget
            val resolvedIconUrl = iconUrlOverride ?: HOTSPOT_ICON_URL

            val wrap = document.createElement("div")
            wrap.asDynamic().className = "marzipano-tool__linkHotspot"
            wrap.asDynamic().style.display = "block"

            val pos = hotspotPosition(viewCenter.first, viewCenter.second)
            val mzHotspot = scene.hotspotContainer().createHotspot(wrap, pos)

            val tracker = HotspotTracker(
                id = nextHotspotId(),
                yaw = viewCenter.first,
                pitch = viewCenter.second,
                target = resolvedTarget,
                iconUrl = resolvedIconUrl,
                kind = kind,
                wrapElement = wrap,
                hotspot = mzHotspot,
            )
            val trackerList = trackersRef.current?.getOrPut(safeIdx) { mutableListOf() }
            trackerList?.add(tracker)
            mountEditorHotspot(safeIdx, tracker)
            if (kind == HotspotKind.Fixed) {
                notifyHotspotsChange(safeIdx)
            }
            return tracker.id
        }

        val addHotspot: () -> Unit = addHotspot@{
            addHotspotInternal()
        }

        val addHotspotWithOptions: (HotspotCreateOptions) -> String? = addHotspotWithOptions@{ options ->
            if (propsRef.current?.pendingDraftHotspotId != null) {
                return@addHotspotWithOptions null
            }
            addHotspotInternal(
                targetOverride = options.targetSceneId,
                iconUrlOverride = options.iconUrl,
                kind = HotspotKind.DraftEditable,
            )
        }

        val removeHotspotById: (String, String) -> Unit = removeHotspotById@{ sceneId, hotspotId ->
            val idx = sceneIndexesById[sceneId] ?: return@removeHotspotById
            val trackerList = trackersRef.current?.get(idx) ?: return@removeHotspotById
            val tracker = trackerList.firstOrNull { it.id == hotspotId } ?: return@removeHotspotById
            val scene = scenesRef.current?.getOrNull(idx)
            if (scene != null) {
                try { scene.hotspotContainer().destroyHotspot(tracker.hotspot) } catch (_: dynamic) {}
            }
            trackerList.removeAll { it.id == hotspotId }
            notifyHotspotsChange(idx)
        }

        val confirmPendingDraftHotspotById: (String, String) -> Boolean =
            confirmPendingDraftHotspotById@{ sceneId, hotspotId ->
                confirmPendingDraftHotspot(sceneId, hotspotId)
            }

        val getPendingDraftHotspotById: (String, String) -> PanoramaLinkHotspot? =
            getPendingDraftHotspotById@{ sceneId, hotspotId ->
                val tracker = findTracker(sceneId, hotspotId)
                if (tracker?.kind == HotspotKind.DraftEditable) {
                    PanoramaLinkHotspot(
                        hotspotId = tracker.id,
                        yaw = tracker.yaw,
                        pitch = tracker.pitch,
                        target = tracker.target,
                        imageName = tracker.iconUrl,
                    )
                } else null
            }

        val beginEditHotspotById: (String, String, HotspotCreateOptions) -> Boolean =
            beginEditHotspotById@{ sceneId, hotspotId, options ->
                val idx = sceneIndexesById[sceneId] ?: return@beginEditHotspotById false
                val tracker = trackersRef.current?.get(idx)?.firstOrNull { it.id == hotspotId }
                    ?: return@beginEditHotspotById false
                if (tracker.kind != HotspotKind.Fixed) return@beginEditHotspotById false
                tracker.target = options.targetSceneId
                tracker.iconUrl = options.iconUrl
                tracker.kind = HotspotKind.DraftEditable
                mountEditorHotspot(idx, tracker)
                true
            }

        val beginMoveHotspotById: (String, String) -> Boolean =
            beginMoveHotspotById@{ sceneId, hotspotId ->
                val idx = sceneIndexesById[sceneId] ?: return@beginMoveHotspotById false
                val tracker = trackersRef.current?.get(idx)?.firstOrNull { it.id == hotspotId }
                    ?: return@beginMoveHotspotById false
                if (tracker.kind != HotspotKind.Fixed) return@beginMoveHotspotById false
                tracker.kind = HotspotKind.DraftEditable
                mountEditorHotspot(idx, tracker)
                true
            }

        val restoreHotspotById: (String, String, PanoramaLinkHotspot) -> Boolean =
            restoreHotspotById@{ sceneId, hotspotId, snapshot ->
                val idx = sceneIndexesById[sceneId] ?: return@restoreHotspotById false
                val tracker = trackersRef.current?.get(idx)?.firstOrNull { it.id == hotspotId }
                    ?: return@restoreHotspotById false
                tracker.yaw = snapshot.yaw
                tracker.pitch = snapshot.pitch
                tracker.target = snapshot.target
                tracker.iconUrl = snapshot.imageName ?: tracker.iconUrl
                tracker.kind = HotspotKind.Fixed
                tracker.hotspot.setPosition(hotspotPosition(snapshot.yaw, snapshot.pitch))
                mountEditorHotspot(idx, tracker)
                true
            }

        /* ── Look-at function: animate camera to a specific yaw/pitch ── */
        val lookAt: (Double, Double) -> Unit = lookAt@{ yaw, pitch ->
            val sceneList = scenesRef.current ?: return@lookAt
            val idx = currentSceneIndexRef.current ?: return@lookAt
            val safeIdx = clampIndex(idx, sceneList.size)
            val scene = sceneList[safeIdx]
            try {
                val view = scene.asDynamic().view()
                // Read current fov so we only change yaw/pitch
                val currentFov = (view.parameters().fov as? Double) ?: 1.2
                val target = js("{}")
                target.yaw = yaw
                target.pitch = pitch
                target.fov = currentFov
                // Animate over 600ms
                view.lookTo(target, js("({transitionDuration: 600})"))
            } catch (_: dynamic) {
                // fallback: set parameters directly without animation
                try {
                    val view = scene.asDynamic().view()
                    val params = view.parameters()
                    params.yaw = yaw
                    params.pitch = pitch
                    view.setParameters(params)
                } catch (_: dynamic) {}
            }
        }

        /* ── Expose handle ── */
        val handle = EditorViewerHandle(
            captureView = captureCurrentView,
            addHotspot = addHotspot,
            addHotspotWithOptions = addHotspotWithOptions,
            removeHotspotById = removeHotspotById,
            confirmPendingDraftHotspotById = confirmPendingDraftHotspotById,
            beginEditHotspotById = beginEditHotspotById,
            beginMoveHotspotById = beginMoveHotspotById,
            restoreHotspotById = restoreHotspotById,
            getPendingDraftHotspotById = getPendingDraftHotspotById,
            getSceneHotspots = { sceneId ->
                val idx = sceneIndexesById[sceneId]
                if (idx != null) collectHotspots(idx) else emptyList()
            },
            lookAt = lookAt,
        )
        props.onHandleReady?.invoke(handle)

        awaitCancellation {
            viewer.destroy()
            container.asDynamic().innerHTML = ""
            scenesRef.current = null
            switchSceneRef.current = null
            trackersRef.current = null
            viewerInstanceRef.current = null
        }
    }

    /* ── Sync active scene by ID without rebuilding ── */
    useEffect(dependencies = arrayOf(props.activeSceneId)) {
        val requestedId = props.activeSceneId ?: return@useEffect
        val idx = sceneIdToIndexRef.current?.get(requestedId) ?: return@useEffect
        if (idx == currentSceneIndexRef.current) return@useEffect
        switchSceneRef.current?.invoke(idx, false)
    }

    div {
        css {
            position = Position.relative
            width = 100.vw
            height = 100.vh
        }
        div {
            css {
                width = 100.vw
                height = 100.vh
                overflow = Overflow.hidden
            }
            ref = viewerRef
        }
        DraftHotspotEditorOverlay {
            pendingDraftSceneId = props.pendingDraftSceneId
            pendingDraftHotspotId = props.pendingDraftHotspotId
            viewerElement = viewerRef.current
            resolveDraftHotspotElement = { sceneId, hotspotId ->
                val tracker = findTracker(sceneId, hotspotId)
                if (tracker?.kind == HotspotKind.DraftEditable) tracker.wrapElement else null
            }
            onCancel = { props.onCancelPendingDraft?.invoke() }
            onConfirm = { _, _ ->
                props.onConfirmPendingDraft?.invoke()
            }
        }
    }
}


/* ─── Shared helpers (same as PanoramaViewer) ──────────────────────────── */







